import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, UserCog, Unlock, LogOut, KeyRound, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [pinModal, setPinModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPin, setNewPin] = useState("");
  const [form, setForm] = useState({ display_name: "", role: "pos_operator", username: "", email: "", password: "", pin: "" });

  const fetchUsers = useCallback(async () => {
    const res = await api.get("/admin/users");
    setUsers(res.data);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditMode(false);
    setEditUserId(null);
    setForm({ display_name: "", role: "pos_operator", username: "", email: "", password: "", pin: "" });
    setModal(true);
  };

  const openEdit = (user) => {
    setEditMode(true);
    setEditUserId(user.id);
    setForm({
      display_name: user.display_name || "",
      role: user.role,
      username: user.username || "",
      email: user.email || "",
      password: "",
      pin: "",
    });
    setModal(true);
  };

  const saveUser = async () => {
    try {
      if (editMode) {
        const payload = {};
        if (form.display_name) payload.display_name = form.display_name;
        if (form.role === 'admin') {
          if (form.username) payload.username = form.username;
          payload.email = form.email;
          if (form.password) payload.password = form.password;
        } else {
          if (form.pin) payload.pin = form.pin;
        }
        await api.put(`/admin/users/${editUserId}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("User created");
      }
      setModal(false); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Error saving user"); }
  };

  const toggleUser = async (user) => {
    await api.put(`/admin/users/${user.id}/toggle`);
    toast.success(`User ${user.is_active ? "deactivated" : "activated"}`);
    fetchUsers();
  };

  const unlockUser = async (user) => {
    await api.post(`/admin/users/${user.id}/unlock`);
    toast.success("User unlocked"); fetchUsers();
  };

  const forceLogout = async (user) => {
    await api.post(`/admin/users/${user.id}/force-logout`);
    toast.success("All sessions terminated"); fetchUsers();
  };

  const resetPin = async () => {
    try {
      await api.post(`/admin/users/${selectedUser.id}/reset-pin`, { pin: newPin });
      toast.success("PIN reset"); setPinModal(false); setNewPin("");
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">User Management</h1>
        <Button onClick={openCreate}
          className="rounded-none bg-black text-white hover:bg-gray-800 text-xs h-9" data-testid="add-user-button">
          <Plus className="w-3 h-3 mr-1" /> Add User
        </Button>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full table-dense">
          <thead><tr className="border-b border-gray-100">
            <th className="text-left">Name</th><th className="text-left">Role</th><th className="text-left">Username</th>
            <th className="text-left">Last Login</th><th className="text-center">Status</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="font-semibold">{u.display_name}</td>
                <td><span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${u.role === 'admin' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                  {u.role === 'admin' ? 'Admin' : 'POS'}
                </span></td>
                <td className="text-xs text-gray-500 font-mono">{u.username || (u.role === 'pos_operator' ? 'PIN login' : '—')}</td>
                <td className="text-gray-500 text-xs">{u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}</td>
                <td className="text-center">
                  <span className={`text-[10px] font-bold uppercase ${u.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                  {u.failed_attempts > 0 && (
                    <span className="text-[10px] text-red-500 ml-1">({u.failed_attempts} fails)</span>
                  )}
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-500"
                      onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} title="Edit">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    {u.role === 'pos_operator' && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs"
                        onClick={() => { setSelectedUser(u); setNewPin(""); setPinModal(true); }}
                        data-testid={`reset-pin-${u.id}`} title="Reset PIN">
                        <KeyRound className="w-3 h-3" />
                      </Button>
                    )}
                    {(u.locked_until || u.failed_attempts > 0) && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-500" onClick={() => unlockUser(u)}
                        data-testid={`unlock-${u.id}`} title="Unlock"><Unlock className="w-3 h-3" /></Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-red-400" onClick={() => forceLogout(u)}
                      data-testid={`force-logout-${u.id}`} title="Force Logout"><LogOut className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => toggleUser(u)}
                      data-testid={`toggle-user-${u.id}`} title={u.is_active ? "Deactivate" : "Activate"}>
                      <UserCog className="w-3 h-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit User Modal */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="rounded-none border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editMode ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Display Name</Label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className="mt-1 rounded-none" data-testid="user-name-input" />
            </div>
            {!editMode && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="pos_operator">POS Operator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.role === 'admin' && (
              <>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Username</Label>
                  <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="mt-1 rounded-none" data-testid="user-username-input" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Email (for password reset)</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 rounded-none" data-testid="user-email-input" placeholder="user@example.com" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">
                    Password {editMode && <span className="text-gray-400 font-normal normal-case">(leave blank to keep current)</span>}
                  </Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-1 rounded-none" data-testid="user-password-input" />
                  <p className="text-[10px] text-gray-400 mt-1">Min 12 chars, upper+lower+digit+special</p>
                </div>
              </>
            )}
            {form.role === 'pos_operator' && !editMode && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">PIN (4-8 digits)</Label>
                <Input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                  className="mt-1 rounded-none font-mono text-lg tracking-[0.3em]" maxLength={8}
                  data-testid="user-pin-input" />
              </div>
            )}
            {form.role === 'pos_operator' && editMode && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">
                  New PIN <span className="text-gray-400 font-normal normal-case">(leave blank to keep current)</span>
                </Label>
                <Input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                  className="mt-1 rounded-none font-mono text-lg tracking-[0.3em]" maxLength={8}
                  data-testid="user-pin-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)} className="rounded-none">Cancel</Button>
            <Button onClick={saveUser} className="rounded-none bg-black text-white hover:bg-gray-800" data-testid="save-user-button">
              {editMode ? "Save Changes" : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset PIN Modal */}
      <Dialog open={pinModal} onOpenChange={setPinModal}>
        <DialogContent className="rounded-none border-gray-200 max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">Reset PIN for {selectedUser?.display_name}</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">New PIN (4-8 digits)</Label>
            <Input value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="mt-1 rounded-none font-mono text-lg tracking-[0.3em]" maxLength={8}
              data-testid="reset-pin-input" />
          </div>
          <DialogFooter>
            <Button onClick={resetPin} className="rounded-none bg-black text-white hover:bg-gray-800" data-testid="confirm-reset-pin">Reset PIN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
